import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Bot, Send, TrendingUp, Euro, Bike, Store, Users, ShoppingBag, Sparkles, Lightbulb, ArrowUpRight, ArrowDownRight, BarChart3, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Quanti ordini abbiamo fatto oggi?",
  "Qual è l'attività con più vendite?",
  "Quanti rider sono online adesso?",
  "Come possiamo aumentare le vendite questo mese?",
];

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><Icon className="h-4 w-4" style={{ color }} /> {label}</div>
      <div className="text-2xl font-display font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function TrendBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-zinc-500">n/d</span>;
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center gap-1 text-sm font-semibold ${up ? "text-[#00E676]" : "text-red-400"}`}><Icon className="h-4 w-4" />{Math.abs(value)}%</span>;
}

function priorityColor(p) {
  if (p === "alta") return "text-red-300 border-red-400/30 bg-red-400/10";
  if (p === "media") return "text-amber-300 border-amber-400/30 bg-amber-400/10";
  return "text-zinc-300 border-white/15";
}

function MiniChart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.revenue || 0));
  return (
    <div className="flex items-end gap-1.5 h-24">
      {series.map((s, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`€${s.revenue}`}>
          <div className="w-full rounded-t bg-[#00E676]/70" style={{ height: `${Math.max(4, (s.revenue / max) * 84)}px` }} />
          <span className="text-[9px] text-zinc-500">{new Date(s.date).toLocaleDateString("it-IT", { weekday: "short" }).slice(0, 2)}</span>
        </div>
      ))}
    </div>
  );
}

export default function CeoAssistant() {
  const [kpis, setKpis] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingIns, setLoadingIns] = useState(false);
  const [sessionId, setSessionId] = useState("ceo-default");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/ceo/assistant/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    api.get("/ceo/assistant/history", { params: { session_id: sessionId } }).then(({ data }) => setMessages(data.map((m) => ({ role: m.role, text: m.text })))).catch(() => {});
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ceo/assistant", { message: q, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.kpis) setKpis(data.kpis);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", text: "Assistente momentaneamente non disponibile. Riprova." }]);
    } finally { setBusy(false); }
  };

  const newChat = () => {
    if (busy) return;
    setSessionId("ceo-" + Date.now().toString(36));
    setMessages([]);
    setInput("");
  };

  const genInsights = async () => {
    setLoadingIns(true);
    try { const { data } = await api.post("/ceo/assistant/insights", {}, { timeout: 60000 }); setInsights(data.insights || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingIns(false); }
  };

  return (
    <div className="space-y-5" data-testid="ceo-assistant">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center"><Bot className="h-6 w-6 text-[#00E676]" /></div>
        <div className="flex-1"><h3 className="font-semibold">Assistente IA · Fuma c'anduma</h3><p className="text-sm text-zinc-400">Analisi dati, KPI e suggerimenti strategici (Claude Sonnet 4.6, sola lettura).</p></div>
        <Button variant="outline" size="sm" data-testid="new-chat" onClick={newChat} disabled={busy} className="border-white/15 rounded-md gap-1.5"><MessageSquarePlus className="h-4 w-4" /> Nuova chat</Button>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={ShoppingBag} label="Ordini oggi" value={kpis.orders_today} color="#00E676" />
          <Kpi icon={Euro} label="Ricavi mese" value={`€${kpis.revenue_month}`} color="#FFD700" />
          <Kpi icon={Bike} label="Rider online" value={`${kpis.riders_online}/${kpis.riders_total}`} color="#7DF9FF" />
          <Kpi icon={Store} label="Attività attive" value={kpis.businesses_approved} color="#E879F9" />
          <Kpi icon={TrendingUp} label="Consegne totali" value={kpis.orders_delivered_total} color="#00E676" />
          <Kpi icon={Euro} label="Valore medio" value={`€${kpis.avg_order_value}`} color="#FFD700" />
          <Kpi icon={Users} label="Clienti" value={kpis.customers} color="#7DF9FF" />
          <Kpi icon={ShoppingBag} label="In corso" value={kpis.orders_in_progress} color="#E879F9" />
        </div>
      )}

      {kpis && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid="ceo-7day-chart">
            <div className="text-xs text-zinc-400 flex items-center gap-1.5 mb-3"><BarChart3 className="h-4 w-4 text-[#00E676]" /> Ricavi ultimi 7 giorni</div>
            <MiniChart series={kpis.series_7d || []} />
          </div>
          <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4 flex flex-col justify-center gap-3" data-testid="ceo-growth">
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Ordini (7gg vs prec.)</span><TrendBadge value={kpis.growth_orders_week} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Ricavi (7gg vs prec.)</span><TrendBadge value={kpis.growth_revenue_week} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Da liquidare alle attività</span><span className="font-semibold text-[#FFD700]">€{Number(kpis.payouts_pending || 0).toFixed(2)}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Contanti da versare (rider)</span><span className="font-semibold text-amber-300">€{Number(kpis.cash_to_remit || 0).toFixed(2)}</span></div>
          </div>
        </div>
      )}

      <div className="bg-[#111114] border border-white/10 rounded-xl p-4" data-testid="ceo-insights">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold flex items-center gap-2"><Lightbulb className="h-4 w-4 text-[#FFD700]" /> Suggerimenti strategici</span>
          <Button size="sm" data-testid="gen-insights" onClick={genInsights} disabled={loadingIns} className="bg-[#00E676] text-black rounded-md">{loadingIns ? "Analizzo…" : (insights ? "Rigenera" : "Genera")}</Button>
        </div>
        {!insights && !loadingIns && <p className="text-sm text-zinc-500">Genera 3-5 azioni concrete basate sui KPI reali della piattaforma.</p>}
        {loadingIns && <p className="text-sm text-zinc-400">Sto analizzando i dati e preparando i suggerimenti…</p>}
        {insights && (
          <div className="grid md:grid-cols-2 gap-3">
            {insights.map((it, i) => (
              <div key={i} data-testid={`insight-${i}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1"><span className="font-medium text-sm">{it.title}</span><span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${priorityColor(it.priority)}`}>{it.priority}</span></div>
                <p className="text-xs text-zinc-400">{it.detail}</p>
                {it.metric && <div className="text-[10px] text-[#00E676] mt-1.5">KPI: {it.metric}</div>}
              </div>
            ))}
            {insights.length === 0 && <p className="text-sm text-zinc-500">Nessun suggerimento disponibile.</p>}
          </div>
        )}
      </div>

      <div className="bg-[#111114] border border-white/10 rounded-xl flex flex-col" style={{ height: 460 }}>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="text-center text-zinc-500 py-8">
              <Sparkles className="h-6 w-6 mx-auto mb-2 text-[#00E676]" />
              <p className="text-sm mb-3">Chiedimi qualcosa sui dati della piattaforma.</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTIONS.map((s, i) => <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#00E676]/50 hover:text-[#00E676] transition-colors">{s}</button>)}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#00E676] text-black" : "bg-[#1C1C1F] border border-white/10 text-zinc-100"}`}>{m.text}</div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-[#1C1C1F] border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-zinc-400">Sto analizzando i dati…</div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-white/10 p-3 flex gap-2">
          <Input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Es. Quanti ordini abbiamo fatto oggi?" className="bg-[#0A0A0C] border-white/10" />
          <Button data-testid="assistant-send" onClick={() => send()} disabled={busy} className="bg-[#00E676] text-black rounded-md"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
